import React from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Pagination
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { 
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Info as InfoIcon
} from '@mui/icons-material'

// Helper styled component for table headers
const SortableHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.8,
  },
}))

// Styled TableCell with controlled width
const StyledTableCell = styled(TableCell)(({ width }) => ({
  maxWidth: width || 'auto',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
}))

const StaffListContainer = ({
  staffMembers,
  filters,
  handleSortChange,
  handlePageChange,
  navigateToStaffDetail,
  roleMapping,
  statusMapping,
  paginationInfo,
  staffLoaded,
  t
}) => {
  // Function to render the sort direction icon
  const renderSortIcon = (column) => {
    if (filters.order_by !== column) return null
    
    return filters.order_dir === 'asc' 
      ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} />
      : <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
  }

  // Function to render status chip with appropriate color
  const renderStatus = (status) => {
    let color = 'default'
    
    switch(status) {
      case 'AC':
        color = 'success'
        break
      case 'PP':
        color = 'warning'
        break
      case 'IN':
        color = 'error'
        break
      default:
        color = 'default'
    }
    
    return (
      <Chip 
        label={t(statusMapping[status])} 
        size="small" 
        color={color}
        variant="outlined"
        sx={{ fontWeight: 500 }}
      />
    )
  }

  // Function to render role chip with appropriate color
  const renderRole = (role) => {
    let color = 'default'
    
    switch(role) {
      case 'PA':
        color = 'primary'
        break
      case 'AD':
        color = 'secondary'
        break
      case 'MA':
        color = 'info'
        break
      default:
        color = 'default'
    }
    
    return (
      <Chip 
        label={t(roleMapping[role])} 
        size="small" 
        color={color}
        sx={{ fontWeight: 500 }}
      />
    )
  }

  // If data is still loading
  if (!staffLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress />
      </Box>
    )
  }

  // If no staff members are found
  if (staffMembers.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <Typography variant="body1" color="text.secondary">
          {t('No staff members found')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('Try adjusting your filters or add a new staff member')}
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ mt: 3, width: '100%' }}>
      <TableContainer 
        component={Paper} 
        sx={{ 
          boxShadow: 1, 
          borderRadius: 2,
          overflow: 'auto',
          width: '100%',
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: 4,
            backgroundColor: 'rgba(0,0,0,0.2)',
          }
        }}
      >
        <Table 
          sx={{ 
            tableLayout: 'fixed',
            '& .MuiTableCell-root': {
              paddingLeft: 2,
              paddingRight: 2,
              '&:first-of-type': { paddingLeft: 3 },
              '&:last-of-type': { paddingRight: 3 }
            }
          }} 
          aria-label="staff members table">
          <TableHead>
            <TableRow>
              <StyledTableCell width="180px">
                <SortableHeader onClick={() => handleSortChange('user__last_name')}>
                  <Typography variant="subtitle2">{t('Staff Member')}</Typography>
                  {renderSortIcon('user__last_name')}
                </SortableHeader>
              </StyledTableCell>
              <StyledTableCell width="120px">
                <SortableHeader onClick={() => handleSortChange('role')}>
                  <Typography variant="subtitle2">{t('Role')}</Typography>
                  {renderSortIcon('role')}
                </SortableHeader>
              </StyledTableCell>
              <StyledTableCell width="120px">
                <SortableHeader onClick={() => handleSortChange('status')}>
                  <Typography variant="subtitle2">{t('Status')}</Typography>
                  {renderSortIcon('status')}
                </SortableHeader>
              </StyledTableCell>
              <StyledTableCell width="180px" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                <Typography variant="subtitle2">{t('Venues')}</Typography>
              </StyledTableCell>
              <StyledTableCell width="120px" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                <SortableHeader onClick={() => handleSortChange('create_date')}>
                  <Typography variant="subtitle2">{t('Added On')}</Typography>
                  {renderSortIcon('create_date')}
                </SortableHeader>
              </StyledTableCell>
              <StyledTableCell width="80px" align="right">
                <Typography variant="subtitle2">{t('Actions')}</Typography>
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {staffMembers.map((staff) => (
              <TableRow
                key={staff.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer' }}
                hover
                onClick={() => navigateToStaffDetail(staff)}
              >
                <StyledTableCell width="180px" component="th" scope="row">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar 
                      src={staff.user.avatar} 
                      alt={`${staff.user.first_name} ${staff.user.last_name}`}
                      sx={{ width: { xs: 32, md: 40 }, height: { xs: 32, md: 40 }, flexShrink: 0 }}
                    >
                      {staff.user.first_name ? staff.user.first_name.charAt(0) : staff.user.email.charAt(0)}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body1" fontWeight={500} sx={{ 
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {staff.user.first_name && staff.user.last_name 
                          ? `${staff.user.first_name} ${staff.user.last_name}`
                          : staff.user.email.split('@')[0]
                        }
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          display: { xs: 'none', sm: 'block' },
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {staff.user.email}
                      </Typography>
                    </Box>
                  </Box>
                </StyledTableCell>
                <StyledTableCell width="120px">
                  {renderRole(staff.role)}
                </StyledTableCell>
                <StyledTableCell width="120px">
                  {renderStatus(staff.status)}
                </StyledTableCell>
                <StyledTableCell width="180px" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  {staff.venues.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: '100%' }}>
                      {staff.venues.slice(0, 2).map((venue) => (
                        <Chip 
                          key={venue.id} 
                          label={venue.name} 
                          size="small" 
                          variant="outlined"
                          sx={{ 
                            maxWidth: '70px',
                            '& .MuiChip-label': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }
                          }}
                        />
                      ))}
                      {staff.venues.length > 2 && (
                        <Chip 
                          label={`+${staff.venues.length - 2}`} 
                          size="small" 
                          variant="outlined" 
                          color="primary"
                        />
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('No venues assigned')}
                    </Typography>
                  )}
                </StyledTableCell>
                <StyledTableCell width="120px" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <Typography variant="body2">
                    {new Date(staff.create_date).toLocaleDateString()}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell width="80px" align="right">
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Tooltip title={t('View Details')}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigateToStaffDetail(staff);
                        }}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Pagination */}
      {paginationInfo.page_total > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={paginationInfo.page_total}
            page={paginationInfo.page_number}
            onChange={(e, page) => handlePageChange(page)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}
    </Box>
  )
}

export default StaffListContainer